import { z } from "zod";

/**
 * Full-name normalisation and duplicate detection (issue #873).
 *
 * The consultant signup wizard sends `full_name` to the backend exactly once,
 * in the POST /start body, and the backend derives the applicant's permanent
 * login handle from it (`full_name.lower().replace(" ", ".")`) along with
 * `first_name` / `last_name`. A doubled value therefore produces a permanently
 * wrong authentication identifier, so the value is normalised and screened for
 * self-repetition before it can leave the browser.
 */

/** Collapse runs of whitespace and trim — `"  Martin   Bonde "` → `"Martin Bonde"`. */
export const normalizeFullName = (value: string): string => value.replace(/\s+/g, " ").trim();

/** Shown to the applicant and asserted on in tests — keep the two in sync. */
export const DOUBLED_FULL_NAME_MESSAGE =
  "This name looks like it was entered twice — please enter it once";

/**
 * True when a name is exactly its own first half repeated.
 *
 * Catches both the observed corruption (`"Martin BondeMartin Bonde"`, joined
 * with no separator) and the space-separated variant.
 *
 * Deliberately conservative — reduplicated names are real, so:
 *   - the character-level rule needs at least 8 characters, which clears
 *     given names such as "Nana", "Coco" or "Lulu";
 *   - the word-level rule needs at least four words, so "Martin Martin" and
 *     other first-name-equals-surname combinations still pass.
 */
export const isDoubledFullName = (value: string): boolean => {
  const normalized = normalizeFullName(value);

  if (normalized.length >= 8 && normalized.length % 2 === 0) {
    const half = normalized.length / 2;
    if (normalized.slice(0, half) === normalized.slice(half)) {
      return true;
    }
  }

  const words = normalized.split(" ");
  if (words.length >= 4 && words.length % 2 === 0) {
    const middle = words.length / 2;
    if (words.slice(0, middle).join(" ") === words.slice(middle).join(" ")) {
      return true;
    }
  }

  return false;
};

/**
 * Zod schema for a `full_name` the backend turns into a permanent username:
 * normalised, length- and character-checked, and screened for
 * self-repetition. Shared by the public signup (#873) and the admin
 * "Add Joiner" form (backend#885).
 */
export const fullNameSchema = z
  .string()
  .transform(normalizeFullName)
  .pipe(
    z
      .string()
      .min(2, "Full name must be at least 2 characters")
      .max(255, "Full name must not exceed 255 characters")
      .regex(
        /^[a-zA-ZÀ-ÿ\s'-]+$/,
        "Full name can only contain letters, spaces, hyphens, and apostrophes"
      )
      .refine(value => !isDoubledFullName(value), DOUBLED_FULL_NAME_MESSAGE)
  );
